import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, addDoc, Timestamp, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import {
    ShoppingBasket,
    Plus,
    Minus,
    ShoppingCart,
    X,
    Check,
    AlertCircle,
    LogOut,
    Menu,
    Package
} from 'lucide-react';
import { auth, db } from './firebase';
import DashboardNav from './DashboardNav';
import { updateAdminSalary } from './adminSalary';

function ProductOrderPage() {
    const [products, setProducts] = useState([]);
    const [selectedProductQuantities, setSelectedProductQuantities] = useState({});
    const [priceControllers, setPriceControllers] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [showOrderSummary, setShowOrderSummary] = useState(false);
    const [showCustomerInfo, setShowCustomerInfo] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);

    // Customer form states
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [customerNotes, setCustomerNotes] = useState('');
    const [deliveryLocation, setDeliveryLocation] = useState('ঢাকা সিটির ভিতরে');

    // Order summary states
    const [confirmedPrices, setConfirmedPrices] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();
    // Cancel / Return / Delivered / Dashboard থেকে আসলে কাস্টমারের তথ্য + product আগে থেকে ভরা থাকে
    const prefillRef = useRef(location.state?.prefill || null);
    // Without Orders পেজ থেকে আসলে: অর্ডার তৈরি হলে ওই withoutorders ডক sell = true হয়
    const withoutOrderIdRef = useRef(location.state?.withoutOrderId || null);
    const [prefillInfo, setPrefillInfo] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserEmail(user.email);
                fetchProducts();
            } else {
                navigate('/login');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    const fetchProducts = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'products'));
            const productsData = [];
            const priceMap = {};

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const product = {
                    paw: data.PAW || 0,
                    pcw: data.PCW || 0,
                    active: data.active || false,
                    buyers: data.buyers || 0,
                    buyingPrice: data.buyingPrice || 0,
                    category: data.category || '',
                    colors: data.colors || [],
                    createdAt: data.createdAt?.toDate() || new Date(),
                    description: data.description || '',
                    discount: data.discount || '',
                    galleryImages: data.galleryImages || [],
                    image: data.image || '',
                    isColorAvailable: data.isColorAvailable || false,
                    isPopular: data.isPopular || false,
                    isSizeAvailable: data.isSizeAvailable || false,
                    isSpecificationAvailable: data.isSpecificationAvailable || false,
                    name: data.name || '',
                    originalPrice: data.originalPrice || 0,
                    price: data.price || 0,
                    productId: data.productId || '',
                    sizes: data.sizes || [],
                    specifications: data.specifications || [],
                    stock: data.stock || 0,
                    updatedAt: data.updatedAt?.toDate() || new Date(),
                    youtubeUrl: data.youtubeUrl || '',
                };
                productsData.push(product);
                priceMap[product.productId] = product.price.toString();
            });

            setProducts(productsData);
            setPriceControllers(priceMap);

            const pf = prefillRef.current;
            if (pf) {
                prefillRef.current = null;
                setCustomerName(pf.name || '');
                setCustomerPhone(pf.phone || '');
                setCustomerAddress(pf.address || '');
                setCustomerNotes(pf.notes || '');
                const sel = {};
                const skipped = [];
                (pf.productIds || []).forEach((id) => {
                    const p = productsData.find((x) => x.productId === id);
                    if (!p) return;
                    if (p.stock > 0) sel[id] = 1; else skipped.push(p.name);
                });
                setSelectedProductQuantities(sel);
                setPrefillInfo({ name: pf.name, phone: pf.phone, selected: Object.keys(sel).length, skipped });
                navigate('/create-order', { replace: true, state: null });
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching products:', error);
            setIsLoading(false);
        }
    };

    const toggleProductSelection = (productId, isSelected) => {
        const product = products.find(p => p.productId === productId);

        if (product.stock === 0 && isSelected) {
            alert('এই প্রোডাক্টটির স্টক নেই, তাই নির্বাচন করা যাবে না।');
            return;
        }

        if (isSelected) {
            setSelectedProductQuantities({ ...selectedProductQuantities, [productId]: 1 });
        } else {
            const newQuantities = { ...selectedProductQuantities };
            delete newQuantities[productId];
            setSelectedProductQuantities(newQuantities);
        }
    };

    const incrementQuantity = (productId) => {
        const product = products.find(p => p.productId === productId);
        const currentQty = selectedProductQuantities[productId] || 0;

        if (currentQty < product.stock) {
            setSelectedProductQuantities({ ...selectedProductQuantities, [productId]: currentQty + 1 });
        } else {
            alert(`${product.name} এর আর স্টক নেই!`);
        }
    };

    const decrementQuantity = (productId) => {
        const currentQty = selectedProductQuantities[productId] || 0;

        if (currentQty > 1) {
            setSelectedProductQuantities({ ...selectedProductQuantities, [productId]: currentQty - 1 });
        } else {
            const newQuantities = { ...selectedProductQuantities };
            delete newQuantities[productId];
            setSelectedProductQuantities(newQuantities);
        }
    };

    const updatePrice = (productId, value) => {
        setPriceControllers({ ...priceControllers, [productId]: value });
    };

    const confirmPrice = (productId) => {
        const price = parseFloat(priceControllers[productId]);
        if (isNaN(price) || price <= 0) {
            alert('সঠিক দাম দিন।');
            return;
        }
        setConfirmedPrices([...confirmedPrices, productId]);
    };

    const calculateTotals = () => {
        let totalPCW = 0;
        let totalPAW = 0;
        let subtotalAmount = 0;

        Object.entries(selectedProductQuantities).forEach(([productId, quantity]) => {
            const product = products.find(p => p.productId === productId);
            const currentPrice = parseFloat(priceControllers[productId]) || product.price;

            totalPCW += product.pcw * quantity;
            totalPAW += product.paw * quantity;
            subtotalAmount += currentPrice * quantity;
        });

        return { totalPCW, totalPAW, subtotalAmount };
    };

    const calculateDeliveryCharge = (totalPCW) => {
        let deliveryCharge = 0;

        if (deliveryLocation === 'ঢাকা সিটির ভিতরে') {
            deliveryCharge = totalPCW * 80;
            if (deliveryCharge < 80 && deliveryCharge > 0) {
                deliveryCharge = 80;
            }
        } else if (deliveryLocation === 'ঢাকা সিটির বাইরে') {
            deliveryCharge = totalPCW * 130;
            if (deliveryCharge < 130 && deliveryCharge > 0) {
                deliveryCharge = 130;
            }
        }

        return deliveryCharge;
    };

    const handleOrderSummary = () => {
        if (Object.keys(selectedProductQuantities).length === 0) {
            alert('অর্ডার করার জন্য কমপক্ষে একটি প্রোডাক্ট সিলেক্ট করুন।');
            return;
        }
        setConfirmedPrices([]);
        setShowOrderSummary(true);
    };

    const proceedToCustomerInfo = () => {
        const selectedCount = Object.keys(selectedProductQuantities).length;
        if (confirmedPrices.length !== selectedCount) {
            alert('প্রোডাক্টের দাম নিশ্চিত করুন');
            return;
        }
        setShowOrderSummary(false);
        setShowCustomerInfo(true);
    };

    const callFraudCheckWebhook = async (orderId, phoneNumber, fromLead = false) => {
        try {
            // Flutter এর NonOrderProductOrderScreen (Without Orders থেকে অর্ডার) আলাদা সার্ভারে webhook পাঠায়
            const url = fromLead
                ? 'https://elevatemart-main-server.sparkx-ai.shop/fraud-check-webhook'
                : 'https://e-commerce-496984660515.asia-south1.run.app/fraud-check-webhook';
            await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderId, phoneNumber }),
            });
        } catch (error) {
            console.error('Fraud check webhook error:', error);
        }
    };

    const confirmOrder = async () => {
        if (!customerName || !customerPhone || !customerAddress) {
            alert('অনুগ্রহ করে গ্রাহকের সব তথ্য পূরণ করুন।');
            return;
        }

        const { totalPCW, totalPAW, subtotalAmount } = calculateTotals();
        const deliveryCharge = calculateDeliveryCharge(totalPCW);
        const totalAmount = subtotalAmount;

        // Without Orders থেকে আসা অর্ডার = Flutter এর মতো 'NORD…' আইডি + non_order: true
        const fromLead = !!withoutOrderIdRef.current;
        const orderId = fromLead
            ? `NORD${Date.now()}${Math.random().toString(16).substring(2, 6)}`
            : `CORD${Date.now()}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
        const clientId = Math.random().toString(36).substring(2) + Date.now().toString(36);
        const now = new Date();

        const cartItems = [];
        let calculatedTotalPAW = 0;
        let calculatedTotalRevenue = 0;

        Object.entries(selectedProductQuantities).forEach(([productId, quantity]) => {
            const product = products.find(p => p.productId === productId);
            const currentPrice = parseFloat(priceControllers[productId]) || product.price;

            calculatedTotalPAW += product.paw * quantity;
            const revenueForProduct = (currentPrice * quantity) - (product.buyingPrice * quantity);
            calculatedTotalRevenue += revenueForProduct;

            cartItems.push({
                PAW: product.paw,
                PCW: product.pcw,
                image: product.image,
                name: product.name,
                originalPrice: product.originalPrice,
                price: fromLead ? Math.trunc(currentPrice) : currentPrice, // Flutter non-order: currentPrice.toInt()
                productId: product.productId,
                quantity: quantity,
                buyingPrice: product.buyingPrice,
                review_added: false,
                selectedColor: product.colors.length > 0 ? product.colors[0] : null,
                selectedSize: product.sizes.length > 0 ? product.sizes[0] : null,
            });
        });

        const actualTotalRevenue = calculatedTotalRevenue - deliveryCharge;

        const orderData = {
            ActualTotalRevenue: actualTotalRevenue,
            AdjustedCustomerDeliveryCharge: deliveryCharge,
            CourierReturnRCV: false,
            CustomerDeliveryCharge: deliveryCharge,
            SteadFastHandOver: false,
            appliedCouponCode: null,
            cartItems: cartItems,
            clientId: clientId,
            created_at: Timestamp.now(),
            customerInfo: {
                address: customerAddress,
                name: customerName,
                notes: customerNotes,
                phone: customerPhone,
            },
            deliveryArea: deliveryLocation,
            deliveryOption: deliveryLocation === "ঢাকা সিটির ভিতরে" ? "insideDhaka" : "outsideDhaka",
            discountAmount: 0,
            orderDate: `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`,
            orderId: orderId,
            orderTime: now.toLocaleTimeString('en-US'),
            paymentDetails: null,
            paymentMethod: 'cashOnDelivery',
            payment_phone_no: null,
            payment_trx_id: null,
            reviewAdded: false,
            status: 'Processing',
            subtotalAmount: subtotalAmount,
            totalAmount: totalAmount,
            totalPAW: calculatedTotalPAW,
            totalPCW: totalPCW,
            totalRevenue: calculatedTotalRevenue,
            user_id: customerPhone,
            order_is_created_by: userEmail,
            ...(fromLead ? { non_order: true } : {}),
        };

        try {
            await setDoc(doc(db, 'orders', orderId), orderData);
            // Without Orders থেকে এলে: ওই lead sell = true + অর্ডারকারী admin এর salary +10 (Flutter এর মতোই)
            if (fromLead) {
                const leadId = withoutOrderIdRef.current;
                try {
                    await updateDoc(doc(db, 'withoutorders', leadId), {
                        sell: true,
                        confirmedBy: userEmail,
                        confirmedAt: serverTimestamp(),
                    });
                    withoutOrderIdRef.current = null;
                    const sal = await updateAdminSalary({
                        amount: 10,
                        actionName: 'Non Order Order Create',
                        isAddition: true,
                        staffEmail: userEmail,
                    });
                    if (!sal.success) console.error('salary update failed:', sal.message);
                } catch (e) {
                    console.error('withoutorders sell update failed:', e);
                }
            }

            await callFraudCheckWebhook(orderId, customerPhone, fromLead);

            alert('অর্ডার সফলভাবে প্লেস করা হয়েছে!');

            // Reset form
            setSelectedProductQuantities({});
            setCustomerName('');
            setCustomerPhone('');
            setCustomerAddress('');
            setCustomerNotes('');
            setDeliveryLocation('ঢাকা সিটির ভিতরে');
            setShowCustomerInfo(false);

            // Reset prices to original
            const resetPrices = {};
            products.forEach(product => {
                resetPrices[product.productId] = product.price.toString();
            });
            setPriceControllers(resetPrices);
        } catch (error) {
            console.error('Error placing order:', error);
            alert(`অর্ডার প্লেস করতে সমস্যা হয়েছে: ${error.message}`);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const selectedProducts = products.filter(p => selectedProductQuantities[p.productId]);
    const selectedCount = Object.keys(selectedProductQuantities).length;
    const { totalPCW, totalPAW, subtotalAmount } = calculateTotals();
    const deliveryCharge = calculateDeliveryCharge(totalPCW);
    const totalAmount = subtotalAmount;

    return (
        <div className="flex min-h-screen bg-gray-50 text-gray-900">
            {/* Sidebar */}
            <DashboardNav
                sidebarOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />

            {/* Main Content */}
            <div className="flex-1 min-w-0 overflow-auto">
                {/* Header */}
                <div className="bg-gray-50 border-b border-gray-200 p-4 sticky top-0 z-10">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 hover:bg-orange-50 rounded-lg transition"
                            >
                                <Menu size={24} />
                            </button>
                            <div className="flex items-center gap-3">
                                <ShoppingBasket className="text-orange-600" size={28} />
                                <h1 className="text-2xl font-bold">প্রোডাক্ট অর্ডার সিস্টেম</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {selectedCount > 0 && (
                                <button
                                    onClick={handleOrderSummary}
                                    className="flex items-center gap-2 bg-orange-400 text-white hover:bg-orange-500 px-4 py-2 rounded-lg transition"
                                >
                                    <ShoppingCart size={20} />
                                    <span>অর্ডার করুন</span>
                                    <span className="bg-red-100 text-red-700 text-gray-900 text-xs px-2 py-1 rounded-full">{selectedCount}</span>
                                </button>
                            )}
                            <button
                                onClick={handleLogout}
                                className="flex items-center gap-2 bg-orange-400 text-white hover:bg-orange-500 px-4 py-2 rounded-lg transition"
                            >
                                <LogOut size={20} />
                                Logout
                            </button>
                        </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2 ml-14">Logged in as: {userEmail}</p>
                </div>

                {prefillInfo && (
                    <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start justify-between gap-3">
                        <div className="text-sm text-blue-800">
                            <p className="font-semibold">
                                {prefillInfo.name || 'কাস্টমার'} ({prefillInfo.phone}) এর তথ্য আগে থেকে ভরা হয়েছে
                                {prefillInfo.selected > 0 ? ` এবং ${prefillInfo.selected}টা product সিলেক্ট করা আছে` : ''}।
                            </p>
                            <p>দেখে নিন, দরকার হলে product বদলান, তারপর "অর্ডার করুন" চাপুন।</p>
                            {prefillInfo.skipped.length > 0 && (
                                <p className="text-red-600 mt-1">স্টক নেই বলে সিলেক্ট হয়নি: {prefillInfo.skipped.join(', ')}</p>
                            )}
                        </div>
                        <button onClick={() => setPrefillInfo(null)} className="text-blue-600 hover:text-blue-900"><X size={18} /></button>
                    </div>
                )}

                {/* Products Table */}
                <div className="p-6">
                    {isLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-500"></div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl overflow-hidden border border-gray-200">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-4 text-left text-sm font-semibold">নির্বাচন</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold">ছবি</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold">নাম</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold">দাম</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold">পরিমাণ</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold">স্টক</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold">ক্যাটাগরি</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {products.map((product) => {
                                            const isSelected = selectedProductQuantities[product.productId] !== undefined;
                                            const quantity = selectedProductQuantities[product.productId] || 0;

                                            return (
                                                <tr key={product.productId} className={`hover:bg-orange-50 transition ${isSelected ? 'bg-orange-50' : ''}`}>
                                                    <td className="px-4 py-4">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => toggleProductSelection(product.productId, e.target.checked)}
                                                            className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-400"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <img
                                                            src={product.image}
                                                            alt={product.name}
                                                            className="w-16 h-16 rounded object-cover"
                                                            onError={(e) => {
                                                                e.target.src = 'https://via.placeholder.com/64';
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-4 font-medium">{product.name}</td>
                                                    <td className="px-4 py-4">
                                                        <span className="bg-green-50 text-green-600 px-3 py-1 rounded font-semibold">
                                                            ৳{product.price}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        {isSelected ? (
                                                            <div className="flex items-center gap-2 border border-gray-300 rounded-lg w-fit">
                                                                <button
                                                                    onClick={() => decrementQuantity(product.productId)}
                                                                    className="p-2 hover:bg-red-50 transition"
                                                                >
                                                                    <Minus size={18} className="text-red-600" />
                                                                </button>
                                                                <span className="px-4 font-bold">{quantity}</span>
                                                                <button
                                                                    onClick={() => incrementQuantity(product.productId)}
                                                                    className="p-2 hover:bg-green-50 transition"
                                                                >
                                                                    <Plus size={18} className="text-green-600" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-500">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className={`px-3 py-1 rounded font-semibold ${product.stock > 0
                                                            ? 'bg-orange-50 text-orange-600'
                                                            : 'bg-red-50 text-red-600'
                                                            }`}>
                                                            {product.stock}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className="bg-orange-50 text-orange-600 px-3 py-1 rounded">
                                                            {product.category}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Order Summary Modal */}
            {showOrderSummary && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-gray-200">
                        <div className="bg-orange-50 p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <ShoppingCart className="text-orange-600" size={28} />
                                    <h2 className="text-2xl font-bold">অর্ডারের সারসংক্ষেপ</h2>
                                </div>
                                <button
                                    onClick={() => setShowOrderSummary(false)}
                                    className="text-gray-500 hover:text-gray-900"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                            {selectedProducts.map((product) => {
                                const quantity = selectedProductQuantities[product.productId];
                                const currentPrice = parseFloat(priceControllers[product.productId]) || product.price;
                                const isPriceConfirmed = confirmedPrices.includes(product.productId);

                                return (
                                    <div key={product.productId} className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-300">
                                        <div className="flex items-start gap-4 mb-4">
                                            <img
                                                src={product.image}
                                                alt={product.name}
                                                className="w-16 h-16 rounded object-cover"
                                            />
                                            <div className="flex-1">
                                                <h3 className="font-bold text-lg">{product.name}</h3>
                                                <p className="text-sm text-gray-500">পরিমাণ: {quantity}</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">মোট PCW:</span>
                                                <span className="font-semibold">{(product.pcw * quantity).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">মোট PAW:</span>
                                                <span className="font-semibold">{(product.paw * quantity).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between col-span-2">
                                                <span className="text-gray-500">প্রোডাক্টের দাম:</span>
                                                <span className="font-semibold">৳{product.price}</span>
                                            </div>
                                        </div>

                                        <div className={`border-2 rounded-lg p-3 mb-3 ${isPriceConfirmed
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-red-500 bg-red-50'
                                            }`}>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    value={priceControllers[product.productId]}
                                                    onChange={(e) => updatePrice(product.productId, e.target.value)}
                                                    disabled={isPriceConfirmed}
                                                    className="flex-1 bg-gray-50 border border-gray-300 rounded px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
                                                    placeholder="ইচ্ছামত দাম বসান"
                                                />
                                                {isPriceConfirmed ? (
                                                    <Check size={24} className="text-green-600" />
                                                ) : (
                                                    <button
                                                        onClick={() => confirmPrice(product.productId)}
                                                        className="bg-orange-100 text-orange-600 hover:bg-orange-200 p-2 rounded transition"
                                                    >
                                                        <Check size={20} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center font-bold text-lg">
                                            <span>মোট মূল্য:</span>
                                            <span className="text-green-600">৳{(currentPrice * quantity).toFixed(2)}</span>
                                        </div>
                                    </div>
                                );
                            })}

                            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                                <div className="space-y-2">
                                    <div className="flex justify-between font-semibold">
                                        <span>সর্বমোট PCW:</span>
                                        <span>{totalPCW.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between font-semibold">
                                        <span>সর্বমোট PAW:</span>
                                        <span>{totalPAW.toFixed(2)}</span>
                                    </div>
                                    <div className="border-t border-orange-200 pt-2 mt-2">
                                        <div className="flex justify-between font-bold text-xl">
                                            <span>সাবটোটাল মূল্য:</span>
                                            <span className="text-orange-600">৳{subtotalAmount.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 p-6 flex justify-end gap-4">
                            <button
                                onClick={() => setShowOrderSummary(false)}
                                className="px-6 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition"
                            >
                                বাতিল
                            </button>
                            {confirmedPrices.length === selectedCount ? (
                                <button
                                    onClick={proceedToCustomerInfo}
                                    className="flex items-center gap-2 px-6 py-2 bg-orange-400 text-white hover:bg-orange-500 rounded-lg transition font-semibold"
                                >
                                    <Check size={20} />
                                    অর্ডার নিশ্চিত করুন
                                </button>
                            ) : (
                                <div className="flex items-center gap-2 px-6 py-2 bg-orange-50 border border-orange-600 rounded-lg text-orange-600">
                                    <AlertCircle size={20} />
                                    <span>প্রোডাক্টের দাম নিশ্চিত করুন</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Customer Info Modal */}
            {showCustomerInfo && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-200">
                        <div className="bg-orange-50 p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold">গ্রাহকের তথ্য</h2>
                                <button
                                    onClick={() => setShowCustomerInfo(false)}
                                    className="text-gray-500 hover:text-gray-900"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">গ্রাহকের নাম</label>
                                    <input
                                        type="text"
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                        placeholder="গ্রাহকের নাম লিখুন"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">ফোন নম্বর</label>
                                    <input
                                        type="tel"
                                        value={customerPhone}
                                        onChange={(e) => setCustomerPhone(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                        placeholder="ফোন নম্বর লিখুন"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">ঠিকানা</label>
                                    <textarea
                                        value={customerAddress}
                                        onChange={(e) => setCustomerAddress(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                                        rows="3"
                                        placeholder="সম্পূর্ণ ঠিকানা লিখুন"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">নোট (ঐচ্ছিক)</label>
                                    <textarea
                                        value={customerNotes}
                                        onChange={(e) => setCustomerNotes(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                                        rows="2"
                                        placeholder="অতিরিক্ত নোট (যদি থাকে)"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">ডেলিভারি এরিয়া:</label>
                                    <div className="bg-gray-50 border border-gray-300 rounded-lg overflow-hidden">
                                        <label className="flex items-center p-4 cursor-pointer hover:bg-gray-100 transition">
                                            <input
                                                type="radio"
                                                name="deliveryLocation"
                                                value="ঢাকা সিটির ভিতরে"
                                                checked={deliveryLocation === 'ঢাকা সিটির ভিতরে'}
                                                onChange={(e) => setDeliveryLocation(e.target.value)}
                                                className="w-4 h-4 text-orange-600 focus:ring-orange-400"
                                            />
                                            <div className="ml-3 flex-1">
                                                <div className="font-semibold">ঢাকা সিটির ভিতরে</div>
                                                <div className="text-sm text-gray-500">
                                                    চার্জ: {totalPCW * 80 >= 80 ? (totalPCW * 80).toFixed(0) : "80"} টাকা
                                                </div>
                                            </div>
                                        </label>

                                        <div className="border-t border-gray-300"></div>

                                        <label className="flex items-center p-4 cursor-pointer hover:bg-gray-100 transition">
                                            <input
                                                type="radio"
                                                name="deliveryLocation"
                                                value="ঢাকা সিটির বাইরে"
                                                checked={deliveryLocation === 'ঢাকা সিটির বাইরে'}
                                                onChange={(e) => setDeliveryLocation(e.target.value)}
                                                className="w-4 h-4 text-orange-600 focus:ring-orange-400"
                                            />
                                            <div className="ml-3 flex-1">
                                                <div className="font-semibold">ঢাকা সিটির বাইরে</div>
                                                <div className="text-sm text-gray-500">
                                                    চার্জ: {totalPCW * 130 >= 130 ? (totalPCW * 130).toFixed(0) : "130"} টাকা
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">ডেলিভারি চার্জ:</span>
                                            <span className="font-bold">৳{deliveryCharge.toFixed(2)}</span>
                                        </div>
                                        <div className="border-t border-orange-200 pt-2 mt-2">
                                            <div className="flex justify-between font-bold text-xl">
                                                <span>মোট প্রদেয় মূল্য:</span>
                                                <span className="text-orange-600">৳{totalAmount.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 p-6 flex justify-end gap-4">
                            <button
                                onClick={() => setShowCustomerInfo(false)}
                                className="px-6 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition"
                            >
                                বাতিল
                            </button>
                            <button
                                onClick={confirmOrder}
                                className="flex items-center gap-2 px-6 py-2 bg-orange-400 text-white hover:bg-orange-500 rounded-lg transition font-semibold"
                            >
                                <ShoppingCart size={20} />
                                অর্ডার নিশ্চিত করুন
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductOrderPage